import {
  Outlet,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import "../styles.css";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { NotFoundPage } from "@/components/site/NotFound";
import { initAnalytics, trackPageView } from "../lib/analytics";


function NotFoundComponent() {
  return <NotFoundPage />;
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "SeaClay Roofing & Spray Foam Insulation LLC" },
      { name: "theme-color", content: "#1c2433" },
      { name: "color-scheme", content: "light" },
      { name: "format-detection", content: "telephone=yes" },
      { property: "og:site_name", content: "SeaClay Roofing & Spray Foam Insulation" },
      { name: "google-site-verification", content: "Sj97rZlUycsC4XRD6ylL7q3QkLaYhBodzAw1CTKquTg" },
    ],
    // Fonts are self-hosted in /fonts.css. Do not add Google Fonts <link>s —
    // they create a fonts.googleapis.com → fonts.gstatic.com chain (~61 KiB).
    links: [
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "describedby", href: "/llms.txt" },
      { rel: "alternate", type: "text/plain", title: "LLMs", href: "/llms.txt" },
      { rel: "alternate", type: "application/json", title: "Agents", href: "/agents.json" },
      {
        rel: "alternate",
        type: "text/markdown",
        title: "Agent instructions",
        href: "/agent-instructions.md",
      },
      {
        rel: "alternate",
        type: "text/markdown",
        title: "Agent runbook",
        href: "/agent-runbook.md",
      },
      { rel: "sitemap", href: "/sitemap.xml" },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <HeadContent />
      </head>
      <body>
        <a href="#main" className="skip-link">
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();

  useEffect(() => {
    initAnalytics();

    const unsubscribe = router.subscribe("onResolved", (event) => {
      trackPageView(event.toLocation.pathname);
    });

    trackPageView(router.state.location.pathname);

    return () => {
      unsubscribe();
    };
  }, [router]);

  return (
    <>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </>
  );
}

